import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Mail,
  Calendar,
  Shield,
  Key,
  Trash2,
  Save,
  AlertTriangle,
  Check,
  X,
  Loader2,
  Smartphone,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@context/AuthContext';
import { authAPI, twoFactorAPI } from '@services/api';
import { ROUTES, VALIDATION } from '@utils/constants';
import Layout from '@components/layout/Layout';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import Alert from '@components/common/Alert';
import TwoFactorSetup, {
  TwoFactorDisable,
  BackupCodesRegenerate,
} from '@components/auth/TwoFactorSetup';
import DataExport from '@components/profile/DataExport';

// Auth provider display names and badges
const AUTH_PROVIDER_INFO: Record<string, { name: string; color: string }> = {
  local: { name: 'Email/Password', color: 'bg-gray-100 text-gray-700' },
  google: { name: 'Google', color: 'bg-red-100 text-red-700' },
  github: { name: 'GitHub', color: 'bg-gray-800 text-white' },
  magic_link: { name: 'Magic Link', color: 'bg-purple-100 text-purple-700' },
};

interface ProfilePageProps {
  /** When true, render without the marketing Layout (e.g. inside the dashboard). */
  embedded?: boolean;
}

const ProfilePage: React.FC<ProfilePageProps> = ({ embedded = false }) => {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const Wrapper = embedded ? React.Fragment : Layout;

  // Profile form state
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
  });
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState(false);

  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Account deletion state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteData, setDeleteData] = useState({
    password: '',
    confirmation: '',
  });
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Two-Factor Authentication state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [backupCodesRemaining, setBackupCodesRemaining] = useState(0);
  const [isTwoFactorLoading, setIsTwoFactorLoading] = useState(true);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [showTwoFactorDisable, setShowTwoFactorDisable] = useState(false);
  const [showBackupCodesRegenerate, setShowBackupCodesRegenerate] = useState(false);

  // Initialize profile data from user
  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
      });
    }
  }, [user]);

  // Fetch 2FA status
  useEffect(() => {
    const fetchTwoFactorStatus = async () => {
      try {
        const status = await twoFactorAPI.getStatus();
        setTwoFactorEnabled(status.enabled);
        setBackupCodesRemaining(status.backup_codes_remaining);
      } catch {
        // 2FA might not be available or user not authenticated
        setTwoFactorEnabled(false);
        setBackupCodesRemaining(0);
      } finally {
        setIsTwoFactorLoading(false);
      }
    };

    if (user) {
      fetchTwoFactorStatus();
    }
  }, [user]);

  // Derived values
  const hasLocalAuth = user?.auth_provider === 'local' || user?.linked_providers?.includes('local');
  const canChangePassword = hasLocalAuth;
  const createdDate = user?.createdAt || user?.created_at;

  // Handle profile update
  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError(null);
    setProfileSuccess(false);

    // Validation
    if (profileData.first_name.length < VALIDATION.NAME_MIN_LENGTH) {
      setProfileError('First name must be at least 2 characters');
      return;
    }
    if (profileData.last_name.length < VALIDATION.NAME_MIN_LENGTH) {
      setProfileError('Last name must be at least 2 characters');
      return;
    }

    setIsProfileLoading(true);

    try {
      const response = await authAPI.updateProfile({
        first_name: profileData.first_name,
        last_name: profileData.last_name,
      });

      // Update local user state
      if (response.data) {
        updateUser({
          first_name: profileData.first_name,
          last_name: profileData.last_name,
          name: `${profileData.first_name} ${profileData.last_name}`.trim(),
        });
      }

      setProfileSuccess(true);
      toast.success('Profile updated successfully');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update profile';
      setProfileError(errorMessage);
      toast.error('Failed to update profile');
    } finally {
      setIsProfileLoading(false);
    }
  };

  // Handle password change
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    // Validation
    if (passwordData.new_password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    if (!/\d/.test(passwordData.new_password)) {
      setPasswordError('Password must contain at least one digit');
      return;
    }
    if (!/[A-Z]/.test(passwordData.new_password)) {
      setPasswordError('Password must contain at least one uppercase letter');
      return;
    }
    if (!/[a-z]/.test(passwordData.new_password)) {
      setPasswordError('Password must contain at least one lowercase letter');
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('Passwords do not match');
      return;
    }

    setIsPasswordLoading(true);

    try {
      await authAPI.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });

      setPasswordSuccess(true);
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
      toast.success('Password changed successfully');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to change password';
      setPasswordError(errorMessage);
      toast.error('Failed to change password');
    } finally {
      setIsPasswordLoading(false);
    }
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    setDeleteError(null);

    // Validation
    if (deleteData.confirmation !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm');
      return;
    }

    if (hasLocalAuth && !deleteData.password) {
      setDeleteError('Password is required to delete your account');
      return;
    }

    setIsDeleteLoading(true);

    try {
      await authAPI.deleteAccount({
        password: hasLocalAuth ? deleteData.password : undefined,
        confirmation: deleteData.confirmation,
      });

      toast.success('Account deleted successfully');
      logout();
      navigate(ROUTES.HOME);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete account';
      setDeleteError(errorMessage);
      toast.error('Failed to delete account');
    } finally {
      setIsDeleteLoading(false);
    }
  };

  // Handle 2FA setup success
  const handleTwoFactorSetupSuccess = async () => {
    try {
      const status = await twoFactorAPI.getStatus();
      setTwoFactorEnabled(status.enabled);
      setBackupCodesRemaining(status.backup_codes_remaining);
    } catch {
      // Refresh page to update status
      setTwoFactorEnabled(true);
    }
  };

  // Handle 2FA disable success
  const handleTwoFactorDisableSuccess = () => {
    setTwoFactorEnabled(false);
    setBackupCodesRemaining(0);
  };

  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'Unknown';
    }
  };

  if (!user) {
    return (
      <Wrapper>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className={embedded ? '' : 'min-h-screen bg-gray-50 py-8'}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Profile Settings</h1>
            <p className="mt-2 text-gray-600">
              Manage your account settings and preferences
            </p>
          </div>

          <div className="space-y-6">
            {/* Account Information (Read-only) */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center">
                  <User size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Account Information
                  </h2>
                  <p className="text-sm text-gray-500">
                    Your account details (some fields are read-only)
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {/* Email (read-only) */}
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">Email Address</p>
                    <p className="font-medium text-gray-900">{user.email}</p>
                  </div>
                  {user.email_verified && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                      <Check size={12} />
                      Verified
                    </span>
                  )}
                </div>

                {/* Auth Provider */}
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Shield className="w-5 h-5 text-gray-400" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">Authentication Method</p>
                    <div className="flex items-center gap-2 mt-1">
                      {user.auth_provider && (
                        <span
                          className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                            AUTH_PROVIDER_INFO[user.auth_provider]?.color ||
                            'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {AUTH_PROVIDER_INFO[user.auth_provider]?.name ||
                            user.auth_provider}
                        </span>
                      )}
                      {user.linked_providers?.map(
                        (provider) =>
                          provider !== user.auth_provider && (
                            <span
                              key={provider}
                              className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                                AUTH_PROVIDER_INFO[provider]?.color ||
                                'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {AUTH_PROVIDER_INFO[provider]?.name || provider}
                            </span>
                          )
                      )}
                    </div>
                  </div>
                </div>

                {/* Account Created */}
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Member Since</p>
                    <p className="font-medium text-gray-900">
                      {formatDate(createdDate)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Update Form */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
                  <User size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Personal Information
                  </h2>
                  <p className="text-sm text-gray-500">
                    Update your name and personal details
                  </p>
                </div>
              </div>

              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="First Name"
                    value={profileData.first_name}
                    onChange={(e) =>
                      setProfileData((prev) => ({
                        ...prev,
                        first_name: e.target.value,
                      }))
                    }
                    placeholder="Enter your first name"
                    required
                    leftIcon={<User size={16} />}
                  />
                  <Input
                    label="Last Name"
                    value={profileData.last_name}
                    onChange={(e) =>
                      setProfileData((prev) => ({
                        ...prev,
                        last_name: e.target.value,
                      }))
                    }
                    placeholder="Enter your last name"
                    required
                    leftIcon={<User size={16} />}
                  />
                </div>

                {profileError && (
                  <Alert
                    type="error"
                    message={profileError}
                    dismissible
                    onDismiss={() => setProfileError(null)}
                  />
                )}

                {profileSuccess && (
                  <Alert
                    type="success"
                    message="Profile updated successfully!"
                    dismissible
                    onDismiss={() => setProfileSuccess(false)}
                  />
                )}

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    variant="primary"
                    loading={isProfileLoading}
                    disabled={isProfileLoading}
                    icon={<Save size={16} />}
                  >
                    Save Changes
                  </Button>
                </div>
              </form>
            </div>

            {/* Password Change Section */}
            {canChangePassword && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center">
                    <Key size={20} />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Change Password
                    </h2>
                    <p className="text-sm text-gray-500">
                      Update your password for enhanced security
                    </p>
                  </div>
                </div>

                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  <Input
                    type="password"
                    label="Current Password"
                    value={passwordData.current_password}
                    onChange={(e) =>
                      setPasswordData((prev) => ({
                        ...prev,
                        current_password: e.target.value,
                      }))
                    }
                    placeholder="Enter your current password"
                    required
                    leftIcon={<Key size={16} />}
                    showPasswordToggle
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      type="password"
                      label="New Password"
                      value={passwordData.new_password}
                      onChange={(e) =>
                        setPasswordData((prev) => ({
                          ...prev,
                          new_password: e.target.value,
                        }))
                      }
                      placeholder="Enter new password"
                      required
                      leftIcon={<Key size={16} />}
                      showPasswordToggle
                      helperText="Min 8 chars, 1 uppercase, 1 lowercase, 1 number"
                    />
                    <Input
                      type="password"
                      label="Confirm New Password"
                      value={passwordData.confirm_password}
                      onChange={(e) =>
                        setPasswordData((prev) => ({
                          ...prev,
                          confirm_password: e.target.value,
                        }))
                      }
                      placeholder="Confirm new password"
                      required
                      leftIcon={<Key size={16} />}
                      showPasswordToggle
                    />
                  </div>

                  {passwordError && (
                    <Alert
                      type="error"
                      message={passwordError}
                      dismissible
                      onDismiss={() => setPasswordError(null)}
                    />
                  )}

                  {passwordSuccess && (
                    <Alert
                      type="success"
                      message="Password changed successfully!"
                      dismissible
                      onDismiss={() => setPasswordSuccess(false)}
                    />
                  )}

                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      variant="primary"
                      loading={isPasswordLoading}
                      disabled={isPasswordLoading}
                      icon={<Key size={16} />}
                    >
                      Update Password
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Two-Factor Authentication Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
                  <Shield size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Two-Factor Authentication
                  </h2>
                  <p className="text-sm text-gray-500">
                    Add an extra layer of security to your account
                  </p>
                </div>
              </div>

              {isTwoFactorLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : twoFactorEnabled ? (
                <div className="space-y-4">
                  {/* 2FA Enabled Status */}
                  <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="w-10 h-10 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
                      <Check size={20} />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-green-800">
                        Two-factor authentication is enabled
                      </p>
                      <p className="text-sm text-green-600">
                        Your account is protected with an authenticator app
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                      <Shield size={14} />
                      Enabled
                    </span>
                  </div>

                  {/* Backup Codes Status */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Key className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">Backup Codes</p>
                        <p className="text-sm text-gray-500">
                          {backupCodesRemaining} codes remaining
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => setShowBackupCodesRegenerate(true)}
                      icon={<RefreshCw size={14} />}
                    >
                      Regenerate
                    </Button>
                  </div>

                  {/* Low Backup Codes Warning */}
                  {backupCodesRemaining <= 3 && backupCodesRemaining > 0 && (
                    <Alert
                      type="warning"
                      message={`You only have ${backupCodesRemaining} backup code${backupCodesRemaining === 1 ? '' : 's'} remaining. Consider regenerating new codes.`}
                    />
                  )}

                  {backupCodesRemaining === 0 && (
                    <Alert
                      type="error"
                      message="You have no backup codes remaining. Regenerate new codes to ensure you can recover your account."
                    />
                  )}

                  {/* Disable 2FA Button */}
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      onClick={() => setShowTwoFactorDisable(true)}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      Disable 2FA
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* 2FA Not Enabled */}
                  <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="w-10 h-10 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center">
                      <AlertTriangle size={20} />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-yellow-800">
                        Two-factor authentication is not enabled
                      </p>
                      <p className="text-sm text-yellow-600">
                        Enable 2FA to add an extra layer of security to your account
                      </p>
                    </div>
                  </div>

                  {/* Benefits List */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">
                      Why enable 2FA?
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-2">
                      <li className="flex items-center gap-2">
                        <Check size={16} className="text-green-500" />
                        Protects your account even if your password is compromised
                      </li>
                      <li className="flex items-center gap-2">
                        <Check size={16} className="text-green-500" />
                        Uses industry-standard TOTP (Time-based One-Time Password)
                      </li>
                      <li className="flex items-center gap-2">
                        <Check size={16} className="text-green-500" />
                        Works with popular authenticator apps like Google Authenticator
                      </li>
                      <li className="flex items-center gap-2">
                        <Check size={16} className="text-green-500" />
                        Backup codes available if you lose access to your phone
                      </li>
                    </ul>
                  </div>

                  {/* Enable 2FA Button */}
                  <div className="flex justify-end">
                    <Button
                      variant="primary"
                      onClick={() => setShowTwoFactorSetup(true)}
                      icon={<Smartphone size={16} />}
                    >
                      Enable 2FA
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Data Export Section */}
            <DataExport />

            {/* Danger Zone - Account Deletion */}
            <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-red-100 text-red-600 rounded-full flex items-center justify-center">
                  <AlertTriangle size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-red-600">Danger Zone</h2>
                  <p className="text-sm text-gray-500">
                    Irreversible and destructive actions
                  </p>
                </div>
              </div>

              <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-medium text-gray-900">Delete Account</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Permanently delete your account and all associated data. This
                      action cannot be undone.
                    </p>
                  </div>
                  <Button
                    variant="danger"
                    onClick={() => setShowDeleteModal(true)}
                    icon={<Trash2 size={16} />}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={() => setShowDeleteModal(false)}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center">
                  <AlertTriangle size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Delete Account
                  </h3>
                  <p className="text-sm text-gray-500">
                    This action is permanent and cannot be undone
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-gray-600 text-sm">
                  Are you sure you want to delete your account? All your data,
                  including order history and preferences, will be permanently
                  removed.
                </p>

                {hasLocalAuth && (
                  <Input
                    type="password"
                    label="Enter your password to confirm"
                    value={deleteData.password}
                    onChange={(e) =>
                      setDeleteData((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    placeholder="Your password"
                    leftIcon={<Key size={16} />}
                    showPasswordToggle
                  />
                )}

                <Input
                  label="Type DELETE to confirm"
                  value={deleteData.confirmation}
                  onChange={(e) =>
                    setDeleteData((prev) => ({
                      ...prev,
                      confirmation: e.target.value,
                    }))
                  }
                  placeholder="DELETE"
                  leftIcon={<AlertTriangle size={16} />}
                />

                {deleteError && (
                  <Alert
                    type="error"
                    message={deleteError}
                    dismissible
                    onDismiss={() => setDeleteError(null)}
                  />
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteData({ password: '', confirmation: '' });
                    setDeleteError(null);
                  }}
                  icon={<X size={16} />}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleDeleteAccount}
                  loading={isDeleteLoading}
                  disabled={
                    isDeleteLoading ||
                    deleteData.confirmation !== 'DELETE' ||
                    (hasLocalAuth && !deleteData.password)
                  }
                  icon={<Trash2 size={16} />}
                >
                  Delete Account
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Two-Factor Setup Modal */}
      <TwoFactorSetup
        isOpen={showTwoFactorSetup}
        onClose={() => setShowTwoFactorSetup(false)}
        onSuccess={handleTwoFactorSetupSuccess}
      />

      {/* Two-Factor Disable Modal */}
      <TwoFactorDisable
        isOpen={showTwoFactorDisable}
        onClose={() => setShowTwoFactorDisable(false)}
        onSuccess={handleTwoFactorDisableSuccess}
      />

      {/* Backup Codes Regenerate Modal */}
      <BackupCodesRegenerate
        isOpen={showBackupCodesRegenerate}
        onClose={() => setShowBackupCodesRegenerate(false)}
      />
    </Wrapper>
  );
};

export default ProfilePage;
